import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { User, Camera, Phone, Mail, GraduationCap, Building2, Save, Calendar, FileText, Upload, Eye, Download, Trash2, CheckCircle, Briefcase } from 'lucide-react';
import { openBlobInNewTab } from '../utils/fileUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';

const ProfilResponsable = ({ user }) => {
  const sigCanvas = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [centreInfo, setCentreInfo] = useState(null);
  const [profile, setProfile] = useState({
    photo: '',
    date_naissance: '',
    diplome: '',
    annee_diplome: '',
    telephone: user.telephone || '',
    email: user.email || '',
    signature: '',
    experience_annees: ''
  });
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    loadProfile();
    loadDocuments();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`/responsables-pedagogiques/${user.user_id}`);
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
        if (response.data.centre_info) {
          setCentreInfo(response.data.centre_info);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La photo ne doit pas dépasser 5 Mo');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile(prev => ({ ...prev, photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`/documents/${user.user_id}`);
      setDocuments(response.data || []);
    } catch (error) {
    }
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    const existingDoc = documents.find(d => d.document_type === docType);
    if (existingDoc) {
      try { await axios.delete(`/documents/${existingDoc.document_id}`); } catch (err) {}
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.user_id);
    formData.append('document_type', docType);
    try {
      await axios.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploadé');
      loadDocuments();
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    }
  };

  const handleViewDocument = async (documentId) => {
    try {
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteChars = atob(doc.file_data);
        const byteArr = new Uint8Array([...byteChars].map(c => c.charCodeAt(0)));
        openBlobInNewTab(new Blob([byteArr], { type: doc.content_type || 'application/pdf' }));
      }
    } catch (error) {
      toast.error("Erreur lors de l'ouverture");
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteChars = atob(doc.file_data);
        const byteArr = new Uint8Array([...byteChars].map(c => c.charCodeAt(0)));
        const url = URL.createObjectURL(new Blob([byteArr]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || doc.filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`/documents/${documentId}`);
      toast.success('Document supprimé');
      loadDocuments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveSignature = () => {
    if (sigCanvas.current) {
      if (sigCanvas.current.isEmpty()) {
        toast.error('Veuillez signer avant de sauvegarder');
        return;
      }
      const signatureData = sigCanvas.current.toDataURL('image/png');
      setProfile(prev => ({ ...prev, signature: signatureData }));
      toast.success('Signature enregistrée');
    }
  };

  const handleClearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`/responsables-pedagogiques/${user.user_id}`, profile);
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profil-responsable">
      {/* Header Bandeau */}
      <div className="bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Photo */}
          <div className="flex-shrink-0 relative">
            <div className="w-24 h-32 bg-white/20 border-4 border-white/40 rounded-lg overflow-hidden">
              {profile.photo ? (
                <img src={profile.photo} alt="Photo" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="h-16 w-16 text-white/60" />
                </div>
              )}
            </div>
            <label className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-lg cursor-pointer hover:bg-slate-100 transition-colors">
              <Camera className="h-5 w-5 text-[#3B82F6]" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoChange}
              />
            </label>
          </div>
          
          {/* Infos principales */}
          <div className="flex-1">
            <p className="text-sm text-white/70 mb-1">Responsable Pédagogique</p>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">
              {user.prenom} {user.nom}
            </h1>
            
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90 mb-3">
              {profile.telephone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  {profile.telephone}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Mail className="h-4 w-4" />
                {profile.email || user.email}
              </span>
            </div>
            
            {centreInfo && (
              <div className="bg-white/10 rounded-lg px-4 py-3">
                <p className="font-semibold flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Centre {centreInfo.nom}
                </p>
              </div>
            )}

            {!centreInfo && (
              <div className="bg-amber-500/30 rounded-lg px-4 py-3">
                <p className="text-sm flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Aucun centre assigné - Contactez l'administration
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations personnelles */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-[#0F172A] mb-6 flex items-center gap-2">
            <User className="h-5 w-5 text-[#3B82F6]" />
            Informations Personnelles
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                placeholder="votre@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="telephone">Téléphone</Label>
              <Input
                id="telephone"
                type="tel"
                value={profile.telephone}
                onChange={(e) => setProfile({ ...profile, telephone: e.target.value })}
                placeholder="+33 6 XX XX XX XX"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_naissance">Date de naissance</Label>
              <Input
                id="date_naissance"
                type="date"
                value={profile.date_naissance}
                onChange={(e) => setProfile({ ...profile, date_naissance: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="experience_annees">Nombre d'années d'expérience</Label>
              <Input
                id="experience_annees"
                type="number"
                min="0"
                value={profile.experience_annees}
                onChange={(e) => setProfile({ ...profile, experience_annees: e.target.value })}
                placeholder="Ex: 5"
                data-testid="rp-experience-annees"
              />
            </div>
          </div>
        </div>

        {/* Diplôme */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-[#0F172A] mb-6 flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-[#8DC63F]" />
            Diplôme et Qualification
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="diplome">Diplôme</Label>
              <Input
                id="diplome"
                value={profile.diplome}
                onChange={(e) => setProfile({ ...profile, diplome: e.target.value })}
                placeholder="Ex: DEJEPS, BPJEPS..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="annee_diplome">Année d'obtention</Label>
              <Input
                id="annee_diplome"
                value={profile.annee_diplome}
                onChange={(e) => setProfile({ ...profile, annee_diplome: e.target.value })}
                placeholder="Ex: 2020"
              />
            </div>
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-[#0F172A] mb-6 flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            Documents
          </h2>
          <div className="overflow-hidden border border-slate-200 rounded-lg" data-testid="rp-documents-table">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Type</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Fichier</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  { type: 'carte_pro', label: 'Carte Professionnelle' },
                  { type: 'diplome', label: 'Diplôme' },
                  { type: 'permis', label: 'Permis' }
                ].map((docType) => {
                  const existingDoc = documents.find(d => d.document_type === docType.type);
                  return (
                    <tr key={docType.type} className={existingDoc ? 'bg-green-50/30' : ''} data-testid={`rp-doc-row-${docType.type}`}>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-slate-700">{docType.label}</span>
                      </td>
                      <td className="px-4 py-3">
                        {existingDoc ? (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm text-slate-700 truncate max-w-[200px]">{existingDoc.filename}</p>
                              {existingDoc.uploaded_at && (
                                <p className="text-xs text-slate-400">{new Date(existingDoc.uploaded_at).toLocaleDateString('fr-FR')}</p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400 italic">Non téléversé</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {existingDoc && (
                            <>
                              <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewDocument(existingDoc.document_id)} title="Voir">
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDocument(existingDoc.document_id, existingDoc.filename)} title="Télécharger">
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteDocument(existingDoc.document_id)} title="Supprimer">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          <div className="relative">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleDocumentUpload(e, docType.type)}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              data-testid={`rp-doc-input-${docType.type}`}
                            />
                            <Button size="sm" className={`h-7 pointer-events-none ${existingDoc ? 'bg-slate-500 hover:bg-slate-600' : 'bg-[#8DC63F] hover:bg-[#7db336]'} text-white`}>
                              <Upload className="h-3.5 w-3.5 mr-1" />
                              {existingDoc ? 'Remplacer' : 'Téléverser'}
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Signature */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-[#0F172A] mb-6">Signature</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="mb-2 block">Dessinez votre signature</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-white">
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="black"
                  canvasProps={{
                    width: 400,
                    height: 150,
                    className: 'w-full'
                  }}
                />
              </div>
              <div className="flex gap-2 mt-2">
                <Button type="button" variant="outline" size="sm" onClick={handleClearSignature}>
                  Effacer
                </Button>
                <Button type="button" size="sm" onClick={handleSaveSignature} className="bg-[#8DC63F] hover:bg-[#7db336]">
                  Valider la signature
                </Button>
              </div>
            </div>
            
            {profile.signature && (
              <div>
                <Label className="mb-2 block">Signature actuelle</Label>
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                  <img 
                    src={profile.signature} 
                    alt="Signature" 
                    className="max-h-32 mx-auto"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bouton de sauvegarde */}
        <div className="flex justify-end">
          <Button 
            type="submit" 
            disabled={saving}
            className="bg-[#8DC63F] hover:bg-[#7db336]"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfilResponsable;
